<template>
  <div>
    <textarea
      ref="messageArea"
      :value="message"
      :placeholder="placeholder"
      class="form-control"/>
    <div
      v-if="editable"
      class="editor-tools clearfix">
      <div
        v-if="!noTags"
        v-click-outside="hideTagsList"
        class="editor-tag-list c-dropdown">
        <span
          class="add-tag"
          @click="customTags ? setCustomTag() : toggleTagsList()">
          <icon
            width="12"
            height="12"
            name="plus"/> Add a tag
        </span>
        <div
          v-show="!customTags && showTagsList"
          class="c-dropdown-list settings-list">
          <div
            class="c-dropdown-close"
            @click="hideTagsList">
            <icon name="times"/>
          </div>
          <div class="c-dropdown-items">
            <div
              v-for="tag in normalizedNotificationTags"
              :key="tag.keyword"
              class="c-dropdown-item"
              @click="setTag(tag)">
              <span>
                {{ tag.name }}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div class="editor-buttons">
        <span
          class="editor-button"
          data-element="b">
          <icon name="bold"/>
        </span>
        <span
          class="editor-button"
          data-element="i">
          <icon name="italic"/>
        </span>
        <span
          class="editor-button"
          data-element="s">
          <icon name="strikethrough"/>
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { position } from 'caret-pos';
import ClickOutside from 'v-click-outside';
import Medium from 'medium.js';
import placeCursorAtEnd from '@/helpers/placeCursorAtEnd';

require('../../assets/js/emojioneArea');

export default {
  name: 'MessageTextArea',
  directives: {
    ClickOutside: ClickOutside.directive,
  },
  props: {
    enterSubmit: {
      type: Boolean,
      default: true,
    },
    liveTrack: {
      type: Boolean,
      default: false,
    },
    noTags: {
      type: Boolean,
      default: false,
    },
    nativeTags: {
      type: Boolean,
      default: false,
    },
    customTags: {
      type: Boolean,
      default: false,
    },
    position: {
      type: String,
      default: 'bottom',
    },
    placeholder: {
      type: String,
      default: 'Type a message',
    },
    editable: {
      type: Boolean,
      default: true,
    },
    disabledTagSources: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      message: '',
      caretPosition: 0,
      showTagsList: false,
      mediumEditor: null,
    };
  },
  computed: {
    ...mapGetters({
      notificationTags: 'notification/notificationTags',
      notificationTagsLoaded: 'notification/notificationTagsLoaded',
    }),
    editor() {
      return this.$refs.messageArea.emojioneArea.editor[0];
    },
    /** Prepares the tags to be used on the template. */
    normalizedNotificationTags() {
      const normalizedTags = this.notificationTags.map(tag => ({
        name: tag.replace('_', ' '),
        keyword: tag,
      }));

      return normalizedTags;
    },
  },
  mounted() {
    const vue = this;

    // TODO - notification tags will be implemented when broadcast feature is finished
    // this.loadAvailableNotificationTags(this.$route.params.merchant_id);

    window.$(this.$refs.messageArea).emojioneArea({
      pickerPosition: this.position,
      filtersPosition: 'bottom',
      tones: false,
      autocomplete: false,
      hidePickerOnBlur: true,
      saveEmojisAs: 'unicode',
      parseTags: !this.nativeTags,
      events: {
        keydown(editor, event) {
          if (vue.enterSubmit && event.keyCode === 13) {
            if (!event.shiftKey) {
              event.preventDefault();
              vue.$emit('messageChanged', this.getText());
            } else {
              vue.$emit('onNewLine', true);
            }
          }
        },
        keyup() {
          if (vue.liveTrack) {
            vue.$nextTick(() => {
              vue.$emit('messageTyping', this.getText());
            });
          }
        },
      },
    });

    this.mediumEditor = new Medium({
      element: this.editor,
      mode: Medium.richMode,
      attributes: null,
      tags: null,
      pasteAsText: false,
      disableExtraSpaces: true,
    });

    window.$('.emojionearea-editor').focusout(() => {
      this.caretPosition = position(this.editor).pos;
    });

    // emojionearea prevents event propagation so we can't catch it on window object and we need to elevate it manually
    window.$('.emojionearea-editor').get(0).addEventListener('paste', this.pasteHandler);

    window.$('.editor-button').mousedown((event) => {
      this.setTextStyle(event.delegateTarget.dataset.element);

      return false;
    });
  },
  updated() {
    this.focusEnd();
  },
  methods: {
    ...mapActions({
      loadAvailableNotificationTags: 'notification/loadAvailableNotificationTags',
    }),
    toggleTagsList() {
      this.showTagsList = !this.showTagsList;
    },
    hideTagsList() {
      this.showTagsList = false;
    },
    setTag(tag) {
      if (!window.$(this.editor).is(':focus')) {
        this.stayFocused();
      }

      const tagTemplate = this.nativeTags ?
        `{{${tag.keyword}}}` :
        `<input type="button" class="editor-tag ellipsis" data-name="{{${tag.keyword}}}" value="${tag.name}"/>&nbsp;`;

      this.$refs.messageArea.emojioneArea.pasteHtmlAtCaret(tagTemplate);
      this.hideTagsList();
    },
    setCustomTag() {
      if (!window.$(this.editor).is(':focus')) {
        this.stayFocused();
      }

      this.$refs.messageArea.emojioneArea.pasteHtmlAtCaret('{{}}');
      this.hideTagsList();
      position(this.editor, this.caretPosition + 2);
    },
    setTextStyle(element) {
      this.caretPosition = position(this.editor).pos;
      this.mediumEditor.invokeElement(element);
      const text = this.normalizeSpaces(this.mediumEditor.value());
      this.mediumEditor.value(text);
      // todo: fix cursor position floating
      position(this.editor, this.caretPosition + 1);
      return false;
    },
    normalizeSpaces(text) {
      text = text
        .replace(/(\S)<b/ig, '$1 <b')
        .replace(/\/b>(\S)/ig, '/b> $1')
        .replace(/(\S)<i/ig, '$1 <i')
        .replace(/\/i>(\S)/ig, '/i> $1')
        .replace(/(\S)<s/ig, '$1 <s')
        .replace(/\/s>(\S)/ig, '/s> $1');

      return text;
    },
    getText() {
      return this.$refs.messageArea.emojioneArea.getText();
    },
    setText(text) {
      this.$refs.messageArea.emojioneArea.setText(text);
      setTimeout(() => {
        this.focusEnd();
      }, 0);
    },
    focus() {
      this.$refs.messageArea.emojioneArea.editor.focus();
    },
    focusEnd() {
      this.$refs.messageArea.emojioneArea.editor.focus();
      placeCursorAtEnd(this.editor);
    },
    stayFocused() {
      this.focus();
      position(this.editor, this.caretPosition);
    },
    isEmpty() {
      return !(this.$refs.messageArea.emojioneArea.getText().trim());
    },
    clear() {
      this.$refs.messageArea.emojioneArea.setText('');
    },
    /**
     * Method emits paste event to parent component which handles it.
     *
     * @param event ClipboardEvent
     */
    pasteHandler(event) {
      this.$emit('clipboardEvent', event);
    },
  },
};
</script>
